import { useState } from "react"
import PropTypes, { number } from "prop-types"
import { Box, Button } from "@planningcenter/tapestry-react"
import Icon from "components/planning_center_icon"
import AttendeeAddons from "./AttendeeAddons"
import AttendeeAssignments from "./AttendeeAssignments"
import AttendeeForms from "./AttendeeForms"
import AttendeePersonalInfo from "./AttendeePersonalInfo"
import { colors } from "components/shared/admin_colors"
import useFlipperFlag from "hooks/use_flipper_flag"
import useApiClient from "hooks/use_api_client"

const Attendee = ({ event, attendee, isActingStaff, isAdmin, registration }) => {
  const [loading, setLoading] = useState(false)

  const promoteWouldExceedCapacity = event.attendeeTypes.find(
    attendeeType => attendeeType.id === attendee.attendeeTypeId
  ).at_maximum_capacity

  const tooltipText =
    "When you promote this attendee, the capacity will be increased automatically."

  const waitlistEnabled = useFlipperFlag("ROLLOUT_detailed_waitlist")
  let status = "complete"

  if (!attendee.isFinalized && isActingStaff) {
    status = "notFinalized"
  } else if (waitlistEnabled && attendee.isWaitlisted) {
    status = "waitlisted"
  } else if (attendee.isCanceled) {
    status = "canceled"
  } else if (attendee.isIncomplete) {
    status = "incomplete"
  }

  const api = useApiClient("registrations")

  const handleWaitlistPromotion = () => {
    setLoading(true)
    return api
      .post({
        url: `/registrations/${registration.id}/promote_waitlist`,
        data: {
          attendee_ids: [attendee.id]
        }
      })
      .then(_ => {
        return Turbolinks.visit()
      })
  }

  return (
    <div
      className={`pane mt-4 mb-1 print-box attendee-status ${attendee.isCanceled ? "status--canceled" : ""
        }`}
      style={{ backgroundColor: status === "waitlisted" ? colors.neutral_080 : null }}
    >
      <div className="p-2 d-f ai-fs ai-c@sm d-f@print ai-c@print mb-1@print">
        <img
          src={attendee.photoThumbnail}
          alt={`${attendee.name}'s avatar`}
          className="mr-2 w-6 h-6 circle"
        />

        <div className="f-1">
          <div className="ml-1@print d-f ai-c jc-sb">
            <div>
              <h2 className="h3 mb-0">
                {attendee.isCanceled ? (
                  <span className="attendee__name">{attendee.name}</span>
                ) : (
                  <a href={attendee.paths.show}>
                    <span className="clickable attendee__name">{attendee.name}</span>
                  </a>
                )}
              </h2>
              <div>
                <div className="d-f">
                  {attendee.attendeeTypeName}
                  {status === "waitlisted" && !attendee.isCanceled && promoteWouldExceedCapacity && (
                    <Box paddingHorizontal={2} data-tooltip={tooltipText}>
                      <Icon
                        symbol="general#exclamation-triangle"
                        style={{ color: colors.warning }}
                        aria-label={tooltipText}
                      />
                    </Box>
                  )}
                </div>
                {status !== "waitlisted" && (
                  <span className="c-secondary"> ({attendee.baseCost})</span>
                )}
              </div>
            </div>
            <div>
              {status === "notFinalized" && (
                <div className="staff-only">
                  <div className="badge status-badge danger-badge">
                    <span className="p-r t-2p mr-4p font-size-4">
                      <Icon symbol="general#exclamation-triangle" />
                    </span>
                    Not Finalized
                  </div>
                </div>
              )}

              {status === "waitlisted" && !attendee.isCanceled && (
                <Button
                  title="Promote"
                  size="sm"
                  theme="create"
                  variant="outline"
                  onClick={handleWaitlistPromotion}
                  spinner={loading}
                />
              )}

              {status === "waitlisted" && attendee.isCanceled && (
                <div className="p-0 badge status-badge status--canceled">
                  <span className="p-r t-2p mr-4p font-size-5">
                    <Icon symbol="general#x" />
                  </span>
                  Waitlist Canceled
                </div>
              )}

              {status === "canceled" && (
                <div className="p-0 badge status-badge status--canceled">
                  <span className="p-r t-2p mr-4p font-size-5">
                    <Icon symbol="general#x" />
                  </span>
                  Canceled
                </div>
              )}

              {status === "incomplete" && (
                <div className="p-0 badge status-badge warning-badge">
                  <span className="p-r t-2p mr-4p font-size-4">
                    <Icon symbol="general#exclamation-triangle" />
                  </span>
                  Incomplete
                </div>
              )}

              {status === "complete" && (
                <div className="p-0 badge status-badge success-badge">
                  <span className="p-r t-2p mr-4p font-size-4">
                    <Icon symbol="general#check" />
                  </span>
                  Complete
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {status !== "waitlisted" && (
        <div>
          {event.hasAddons && <AttendeeAddons attendee={attendee} />}

          <AttendeePersonalInfo attendee={attendee} isAdmin={isAdmin} event={event} />

          {event.hasForms && <AttendeeForms attendee={attendee} />}

          {event.hasAssignments && <AttendeeAssignments attendee={attendee} event={event} />}
        </div>
      )}
    </div>
  )
}

Attendee.propTypes = {
  isActingStaff: PropTypes.bool,
  isAdmin: PropTypes.bool,
  event: PropTypes.shape({
    hasAddons: PropTypes.bool,
    hasAttendeeQuestions: PropTypes.bool,
    hasForms: PropTypes.bool,
    hasAssignments: PropTypes.bool,
    attendeeTypes: PropTypes.array
  }),
  registration: PropTypes.shape({
    id: number
  }),
  attendee: PropTypes.shape({
    id: PropTypes.number,
    attendeeTypeId: PropTypes.number,
    name: PropTypes.string,
    photoThumbnail: PropTypes.string,
    isVisibleEmergencyContact: PropTypes.bool,
    optionalFields: PropTypes.array,
    requiredFields: PropTypes.array,
    attendeeTypeName: PropTypes.string,
    baseCost: PropTypes.string,
    isFinalized: PropTypes.bool,
    isCanceled: PropTypes.bool,
    isMissingPersonalInfo: PropTypes.bool,
    isIncomplete: PropTypes.bool,
    isWaitlisted: PropTypes.bool,
    hasAllQuestionsAnswered: PropTypes.bool,
    missingFormsCount: PropTypes.number,
    person: PropTypes.shape({
      accountCenterId: PropTypes.number
    }),
    paths: PropTypes.shape({
      show: PropTypes.string
    })
  })
}

export default Attendee
